import type MvInputParams from "models/MvInputParams";
import { CountryCode } from "models/CountryCode";
import styles from "./Filters.module.scss";
import {
    cloneElement,
    isValidElement,
    ComponentChildren,
    ComponentChild,
    VNode,
    Fragment,
    h,
    toChildArray,
} from "preact";
import { useState } from "preact/hooks";
import { Text } from "preact-i18n";
import ArrowIcon from "icons/ArrowIcon";
//import { withoutKeys } from "services/helpers";

interface FiltersProps {
    isOpen: boolean;
    params: MvInputParams;
    addressId: string;
    children?: ComponentChildren;
    countryCode?: CountryCode;
    onUpdateParams(addressId: string, params: Partial<MvInputParams>): void;
}

export interface FilterItemProps<T = unknown> {
    addressId?: string;
    labelId?: string;
    fieldName: string;
    value?: T;
    params?: MvInputParams;
    onChange?(value: T): void;
    onUpdateParams?(addressId: string, params: Partial<MvInputParams>): void;
    countryCode?: CountryCode | CountryCode[] | "all";
    more?: boolean;
    half?: boolean;
}

const isFilterItem = (child: ComponentChild): child is VNode<FilterItemProps> =>
    isValidElement(child) &&
    typeof (child.props as Partial<FilterItemProps>).fieldName === "string";

const Filters = (props: FiltersProps) => {
    const { isOpen, params, children, countryCode, addressId, onUpdateParams } =
        props;

    const [showMore, setShowMore] = useState(false);

    const showFilterItem = (filterItemProps: FilterItemProps) => {
        if (filterItemProps?.countryCode == undefined) {
            return true;
        }
        if (
            Array.isArray(countryCode) &&
            countryCode.includes(filterItemProps.countryCode)
        ) {
            return true;
        }
        if (typeof countryCode === "string") {
            return (
                countryCode === filterItemProps.countryCode ||
                filterItemProps.countryCode == "all"
            );
        }
        return false;
    };

    const mapFilterItemWithProps = (
        child: import("preact").VNode<FilterItemProps>,
    ) => {
        return cloneElement(child, {
            value: params[child.props.fieldName as keyof MvInputParams],
            addressId: addressId,
            params: params,
            onUpdateParams: onUpdateParams,
            onChange: child.props.onChange
                ? child.props.onChange
                : (value: unknown) =>
                      onUpdateParams(addressId, {
                          [child.props.fieldName]: value,
                      }),
        });
    };

    let showMoreLinkSet = false;
    return (
        <div
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            className={`${styles.filter} ${
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                isOpen ? styles.open : ""
            }`}
        >
            {toChildArray(children).map((child) => {
                if (isFilterItem(child) && showFilterItem(child.props)) {
                    const showMoreLink = child.props?.more && !showMoreLinkSet;
                    if (showMoreLink) {
                        showMoreLinkSet = true;
                    }
                    return (
                        <Fragment>
                            {showMoreLink && (
                                <div
                                    className={`${styles.filterItem} ${
                                        !showMore && styles.moreClosed
                                    }`}
                                >
                                    <button
                                        onClick={() => setShowMore(!showMore)}
                                        className={styles.moreButton}
                                    >
                                        <ArrowIcon
                                            className={`${styles.arrowIcon} ${
                                                showMore && styles.open
                                            }`}
                                            fill={"currentColor"}
                                        />
                                        <Text
                                            id={
                                                showMore
                                                    ? "filters.buttons.showLess"
                                                    : "filters.buttons.showMore"
                                            }
                                        />
                                    </button>
                                </div>
                            )}
                            <div
                                className={`
                                    ${styles.filterItem}
                                    ${child.props?.half && styles.half}
                                    ${
                                        child.props?.more && !showMore
                                            ? styles.hidden
                                            : ""
                                    }
                                `}
                            >
                                {mapFilterItemWithProps(child)}
                            </div>
                        </Fragment>
                    );
                }
            })}
        </div>
    );
};

export default Filters;
